'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';

// Helper function to check if file is a video
const isVideo = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext === 'webm' || ext === 'mp4';
};

// Skeleton loader for carousel items
const CarouselSkeleton = () => (
  <div className="flex-shrink-0 w-32 h-40 sm:w-40 sm:h-52 relative rounded-lg overflow-hidden bg-white/5 animate-pulse">
    <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5"></div>
  </div>
);

// Carousel item component with loading state
const CarouselItem = ({ src, index }: { src: string; index: number }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const videoTypes = isVideo(src);
  
  // Prevent right-click and drag actions
  const preventActions = (e: React.MouseEvent | React.DragEvent) => {
    e.preventDefault();
    return false;
  };
  
  return (
    <div 
      className="flex-shrink-0 w-32 h-40 sm:w-40 sm:h-52 relative rounded-lg overflow-hidden bg-black/20 select-none"
      onContextMenu={preventActions}
      onDragStart={preventActions}
    >
      {!isLoaded && (
        <div className="absolute inset-0 bg-white/5 animate-pulse"></div>
      )}
      {videoTypes ? (
        <video
          src={`/carousel/${src}`}
          autoPlay
          loop
          muted
          playsInline
          className={`w-full h-full object-cover transition-opacity duration-700 pointer-events-none select-none ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
          onLoadedData={() => setIsLoaded(true)}
          onContextMenu={preventActions}
          draggable={false}
        />
      ) : (
        <Image
          src={`/carousel/${src}`}
          alt={`Carousel media ${index + 1}`}
          fill
          className={`object-cover transition-opacity duration-700 pointer-events-none select-none ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
          unoptimized={src.endsWith('.gif')}
          onLoad={() => setIsLoaded(true)}
          draggable={false}
        />
      )}
      {/* Transparent overlay to block interactions */}
      <div 
        className="absolute inset-0 z-10" 
        onContextMenu={preventActions}
        onDragStart={preventActions}
      />
    </div>
  );
};

export default function Carousel({ mediaFiles }: { mediaFiles: string[] }) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to ensure smooth render
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!isReady) {
    return (
      <div className="w-full overflow-hidden relative mt-4 sm:mt-6 md:mt-8">
        <div className="flex gap-3 sm:gap-4">
          {[...Array(6)].map((_, i) => (
            <CarouselSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Triple the content for ultra-smooth infinite loop
  const tripleMedia = [...mediaFiles, ...mediaFiles, ...mediaFiles];

  return (
    <div className="w-full overflow-hidden relative mt-4 sm:mt-6 md:mt-8 opacity-0 animate-fadeIn">
      
      {/* Fade overlays - smaller on mobile. Fade into the xray canvas base color. */}
      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-[#05060b] to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-[#05060b] to-transparent z-10 pointer-events-none"></div>
      
      {/* Scrolling container - seamless infinite scroll */}
      <div className="flex gap-3 sm:gap-4 animate-scroll-seamless">
        {tripleMedia.map((media, index) => (
          <CarouselItem key={`media-${index}`} src={media} index={index} />
        ))}
      </div>
    </div>
  );
}

